import {
	FileProductCalculatedValues,
	FileProductInputLike,
	FileProductOptionCalculatedValues,
	FileProductOptionInputLike,
	AdditionalFeeLike,
	AdditionalFeeOutput,
} from "./types";

import { bigMath } from "../helpers";
import Big from "big.js";

import { DISCOUNT_TYPE, FIX_DISCOUNT_TYPE } from "~served/configs/enums";

import { AdditionalFeeInput } from "./types";

export const getAdditionalFeeAmount = (
	originalAmount: number,
	additionalFeeInput: AdditionalFeeInput,
) => {
	let additionalFeeAmount = 0;

	if (additionalFeeInput.type === DISCOUNT_TYPE.AMOUNT) {
		additionalFeeAmount = additionalFeeInput.value;
	}

	if (additionalFeeInput.type === DISCOUNT_TYPE.PERCENTAGE) {
		additionalFeeAmount = new Big(originalAmount)
			.mul(additionalFeeInput.value)
			.div(100)
			.round(2)
			.toNumber();
	}

	const additionalFeeedAmount = new Big(originalAmount)
		.minus(additionalFeeAmount)
		.round(2)
		.toNumber();

	return { additionalFeeAmount, additionalFeeedAmount };
};

export const getAppliedAdditionalFeeByProducts = <
	TFileProduct extends
		FileProductInputLike<FileProductCalculatedValues> = FileProductInputLike<FileProductCalculatedValues>,
	TFileProductOption extends
		FileProductOptionInputLike<FileProductOptionCalculatedValues> = FileProductOptionInputLike<FileProductOptionCalculatedValues>,
>(
	products: FileProductInputLike<TFileProduct, TFileProductOption>[],
	additionalFee: AdditionalFeeLike,
) => {
	const eligibleProducts: Record<string, AdditionalFeeOutput> = {};

	if (!additionalFee.configs.length)
		return { additionalFee: null, eligibleProducts, additionalFeeAmount: 0 };

	if (isOrderAdditionalFee(additionalFee))
		return { additionalFee: null, eligibleProducts, additionalFeeAmount: 0 };

	products.forEach((fileProduct) => {
		const productTotalPrice = fileProduct.subtotal;

		additionalFee.configs.forEach((config) => {
			switch (config.type) {
				case FIX_DISCOUNT_TYPE.PRODUCTS:
					if (
						config.products.map(String).includes(fileProduct.product.toString())
					) {
						const { additionalFeeAmount } = getAdditionalFeeAmount(
							productTotalPrice,
							{
								type: config.discountType,
								value: config.discountValue,
							},
						);

						if (!eligibleProducts[fileProduct._id.toString()]) {
							eligibleProducts[fileProduct._id.toString()] = {
								type: config.discountType,
								value: config.discountValue,
								amount:
									additionalFeeAmount > productTotalPrice
										? productTotalPrice
										: additionalFeeAmount,
								additionalFee: additionalFee._id,
							};
						} else if (eligibleProducts[fileProduct._id.toString()]) {
							if (
								(additionalFee.isOfferingHigherValue &&
									additionalFeeAmount >
										eligibleProducts[fileProduct._id.toString()].value) ||
								(!additionalFee.isOfferingHigherValue &&
									additionalFeeAmount <
										eligibleProducts[fileProduct._id.toString()].value)
							) {
								eligibleProducts[fileProduct._id.toString()] = {
									type: config.discountType,
									value: config.discountValue,
									amount:
										additionalFeeAmount > productTotalPrice
											? productTotalPrice
											: additionalFeeAmount,
									additionalFee: additionalFee._id,
								};
							}
						}
					}
					break;
				case FIX_DISCOUNT_TYPE.CATEGORIES:
					if (
						config.categories
							.map(String)
							.includes(fileProduct.category.toString())
					) {
						const { additionalFeeAmount } = getAdditionalFeeAmount(
							productTotalPrice,
							{
								type: config.discountType,
								value: config.discountValue,
							},
						);

						if (!eligibleProducts[fileProduct._id.toString()]) {
							eligibleProducts[fileProduct._id.toString()] = {
								type: config.discountType,
								value: config.discountValue,
								amount:
									additionalFeeAmount > productTotalPrice
										? productTotalPrice
										: additionalFeeAmount,
								additionalFee: additionalFee._id,
							};
						} else if (eligibleProducts[fileProduct._id.toString()]) {
							if (
								(additionalFee.isOfferingHigherValue &&
									additionalFeeAmount >
										eligibleProducts[fileProduct._id.toString()].value) ||
								(!additionalFee.isOfferingHigherValue &&
									additionalFeeAmount <
										eligibleProducts[fileProduct._id.toString()].value)
							) {
								eligibleProducts[fileProduct._id.toString()] = {
									type: config.discountType,
									value: config.discountValue,
									amount:
										additionalFeeAmount > productTotalPrice
											? productTotalPrice
											: additionalFeeAmount,
									additionalFee: additionalFee._id,
								};
							}
						}
					}
					break;
				case FIX_DISCOUNT_TYPE.PRODUCT_TYPES:
					if (config.types.map(String).includes(fileProduct.type.toString())) {
						const { additionalFeeAmount } = getAdditionalFeeAmount(
							fileProduct.subtotal,
							{
								type: config.discountType,
								value: config.discountValue,
							},
						);

						if (!eligibleProducts[fileProduct._id.toString()]) {
							eligibleProducts[fileProduct._id.toString()] = {
								type: config.discountType,
								value: config.discountValue,
								amount:
									additionalFeeAmount > productTotalPrice
										? productTotalPrice
										: additionalFeeAmount,
								additionalFee: additionalFee._id,
							};
						} else if (eligibleProducts[fileProduct._id.toString()]) {
							if (
								(additionalFee.isOfferingHigherValue &&
									additionalFeeAmount >
										eligibleProducts[fileProduct._id.toString()].value) ||
								(!additionalFee.isOfferingHigherValue &&
									additionalFeeAmount <
										eligibleProducts[fileProduct._id.toString()].value)
							) {
								eligibleProducts[fileProduct._id.toString()] = {
									type: config.discountType,
									value: config.discountValue,
									amount:
										additionalFeeAmount > productTotalPrice
											? productTotalPrice
											: additionalFeeAmount,
									additionalFee: additionalFee._id,
								};
							}
						}
					}
					break;
			}
		});
	});

	return {
		additionalFee,
		eligibleProducts,
		additionalFeeAmount: Object.values(eligibleProducts).reduce(
			(pre, cur) => bigMath.add(pre, cur.value),
			0,
		),
	};
};

export const getAppliedAdditionalFeeByReceipt = (
	amount: number,
	additionalFee: AdditionalFeeLike,
) => {
	let discountAmount = 0;

	if (!additionalFee.configs.length || !amount)
		return {
			additionalFee: additionalFee ?? null,
			additionalFeeAmount: discountAmount,
		};

	if (!isOrderAdditionalFee(additionalFee))
		return { additionalFee: null, additionalFeeAmount: discountAmount };

	additionalFee.configs.forEach((config) => {
		const { additionalFeeAmount } = getAdditionalFeeAmount(amount, {
			type: config.discountType,
			value: config.discountValue,
		});
		if (
			(additionalFee.isOfferingHigherValue &&
				additionalFeeAmount > additionalFeeAmount) ||
			(!additionalFee.isOfferingHigherValue &&
				additionalFeeAmount < additionalFeeAmount)
		) {
			discountAmount = additionalFeeAmount;
		}
	});

	return {
		additionalFee,
		additionalFeeAmount: discountAmount,
	};
};

export const isOrderAdditionalFee = (additionalFee: AdditionalFeeLike) => {
	return additionalFee.configs.some(
		(config) =>
			config.type === FIX_DISCOUNT_TYPE.ORDERS ||
			config.type === FIX_DISCOUNT_TYPE.AGENTS ||
			config.type === FIX_DISCOUNT_TYPE.CLIENTS,
	);
};
